import React, { createContext, useContext, useState, useEffect } from 'react';
import { getCart, addToCart as apiAddToCart, updateCartQuantity, removeFromCart as apiRemoveFromCart, clearCart as apiClearCart } from '../utils/api';
import { useAuth } from './AuthContext';
import { productPrices } from '../utils/productPricing';
import { cartLineKey, isSameLine } from '../utils/productVariants';
import { isOffline, notifyOffline } from '../utils/network';
import { trackExperiment } from '../utils/experiments';

/**
 * Price a guest-bag line the way the server prices a cart line
 * (utils/pricing.effective_price): the sale price when it is a positive
 * number below retail, else retail. Mirrors GET /cart's
 * {price, total, regular_price, on_sale} so the Bag renders both the same.
 */
export const priceGuestLine = (product, quantity) => {
  const { retail, final } = productPrices(product);
  return {
    price: final,
    regular_price: retail,
    on_sale: final < retail,
    total: final * quantity,
  };
};

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [cart, setCart] = useState({ items: [], subtotal: 0, total_items: 0 });
  const [loading, setLoading] = useState(false);
  const GUEST_CART_KEY = 'anum360_guest_cart';

  // Load cart on mount and when auth changes
  useEffect(() => {
    if (token) {
      fetchCart();
      // Sync guest cart to server on login
      syncGuestCartToServer();
    } else {
      loadGuestCart();
    }
  }, [token]);

  // Load guest cart from localStorage
  const loadGuestCart = async () => {
    try {
      const guestCartData = localStorage.getItem(GUEST_CART_KEY);
      if (guestCartData) {
        const guestCart = JSON.parse(guestCartData);
        // Fetch fresh product details
        const items = await Promise.all(
          guestCart.items.map(async (item) => {
            try {
              // Fetch individual product instead of all 1000
              const { getProduct } = await import('../utils/api');
              const product = await getProduct(item.product_id).catch(() => null);
              if (product) {
                return {
                  ...item,
                  product: product,
                  ...priceGuestLine(product, item.quantity),
                };
              }
              return null;
            } catch (error) {
              if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to fetch product:', error);
              return null;
            }
          })
        );
        
        const validItems = items.filter(item => item !== null);
        const subtotal = validItems.reduce((sum, item) => sum + item.total, 0);
        const total_items = validItems.reduce((sum, item) => sum + item.quantity, 0);
        
        setCart({ items: validItems, subtotal, total_items });
      } else {
        setCart({ items: [], subtotal: 0, total_items: 0 });
      }
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to load guest cart:', error);
      setCart({ items: [], subtotal: 0, total_items: 0 });
    }
  };

  // Save guest cart to localStorage
  const saveGuestCart = (cartData) => {
    try {
      const guestCartData = {
        items: cartData.items.map(item => ({
          product_id: item.product_id,
          quantity: item.quantity,
          variant_id: item.variant_id || null
        }))
      };
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(guestCartData));
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to save guest cart:', error);
    }
  };

  // Sync guest cart to server when user logs in
  const syncGuestCartToServer = async () => {
    try {
      const guestCartData = localStorage.getItem(GUEST_CART_KEY);
      if (guestCartData && token) {
        const guestCart = JSON.parse(guestCartData);
        // Add each item to the server bag. A line that failed for a passing
        // reason (offline, server error, rate limit, session) stays in the
        // guest bag for the next sign-in; one the server refused (sold out,
        // gone, invalid) is dropped.
        const kept = [];
        for (const item of guestCart.items || []) {
          try {
            await apiAddToCart(token, item.product_id, item.variant_id, item.quantity);
          } catch (error) {
            const status = error?.status;
            if (!status || status >= 500 || status === 429 || status === 401) kept.push(item);
            if(process.env.NODE_ENV==='development') console.error('Failed to sync item:', error);
          }
        }
        if (kept.length) localStorage.setItem(GUEST_CART_KEY, JSON.stringify({ items: kept }));
        else localStorage.removeItem(GUEST_CART_KEY);
        // Fetch updated cart
        await fetchCart();
      }
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to sync guest cart:', error);
    }
  };

  // Fetch server cart (for logged-in users)
  const fetchCart = async () => {
    if (!token) return;
    try {
      setLoading(true);
      const cartData = await getCart(token);
      setCart(cartData);
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to fetch cart:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart (supports both guest and logged-in users)
  const addItem = async (productId, variantId = null, quantity = 1) => {
    // Offline: one clear message (toast id dedupes repeated taps); callers get offline: true.
    if (isOffline()) return { success: false, offline: true, error: notifyOffline() };
    try {
      if (token) {
        // Logged-in user: use server API
        await apiAddToCart(token, productId, variantId, quantity);
        await fetchCart();
      } else {
        // Guest user: use localStorage — fetch single product
        const { getProduct } = await import('../utils/api').catch(() => ({ getProduct: null }));
        const product = getProduct ? await getProduct(productId).catch(() => null) : null;
        
        if (!product) {
          return { success: false, error: 'Product not found' };
        }

        // One line per product + size/colour, like the server cart (G16).
        const existingItemIndex = cart.items.findIndex(item => isSameLine(item, productId, variantId));
        
        let updatedItems;
        if (existingItemIndex >= 0) {
          // Update quantity of existing item (re-priced from the fresh product)
          updatedItems = [...cart.items];
          const existing = updatedItems[existingItemIndex];
          const nextQuantity = existing.quantity + quantity;
          updatedItems[existingItemIndex] = {
            ...existing,
            product,
            quantity: nextQuantity,
            ...priceGuestLine(product, nextQuantity),
          };
        } else {
          // Add new item
          const newItem = {
            product_id: productId,
            variant_id: variantId,
            quantity: quantity,
            product: product,
            ...priceGuestLine(product, quantity),
          };
          updatedItems = [...cart.items, newItem];
        }

        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
        const total_items = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
        
        const updatedCart = { items: updatedItems, subtotal, total_items };
        setCart(updatedCart);
        saveGuestCart(updatedCart);
      }
      trackExperiment('add_to_bag'); // A/B testing (§6n): analytics consent only
      return { success: true };
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to add item:', error);
      return { success: false, error: error.message };
    }
  };

  // The server path for a line: its `id` (G16), else the product id (older carts).
  const findLine = (lineKey) => cart.items.find(item => cartLineKey(item) === lineKey)
    || cart.items.find(item => (item.product_id || item.product?.id) === lineKey);
  const serverRef = (lineKey) => {
    const line = findLine(lineKey);
    return line?.id || line?.product_id || line?.product?.id || lineKey;
  };

  // Update quantity of one line (`lineKey` = cartLineKey(item); a product id also works)
  const updateQuantity = async (lineKey, quantity) => {
    try {
      if (token) {
        // Logged-in user: use server API
        await updateCartQuantity(token, serverRef(lineKey), quantity);
        await fetchCart();
      } else {
        // Guest user: update localStorage
        const target = findLine(lineKey);
        const updatedItems = cart.items.map(item => {
          if (item === target) {
            return {
              ...item,
              quantity: quantity,
              total: item.price * quantity
            };
          }
          return item;
        });

        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
        const total_items = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
        
        const updatedCart = { items: updatedItems, subtotal, total_items };
        setCart(updatedCart);
        saveGuestCart(updatedCart);
      }
      return { success: true };
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to update quantity:', error);
      return { success: false, error: error.message };
    }
  };

  // Remove one line (`lineKey` = cartLineKey(item); a product id also works)
  const removeItem = async (lineKey) => {
    try {
      if (token) {
        // Logged-in user: use server API
        await apiRemoveFromCart(token, serverRef(lineKey));
        await fetchCart();
      } else {
        // Guest user: update localStorage
        const target = findLine(lineKey);
        const updatedItems = cart.items.filter(item => item !== target);
        
        const subtotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
        const total_items = updatedItems.reduce((sum, item) => sum + item.quantity, 0);
        
        const updatedCart = { items: updatedItems, subtotal, total_items };
        setCart(updatedCart);
        saveGuestCart(updatedCart);
      }
      return { success: true };
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to remove item:', error);
      return { success: false, error: error.message };
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      if (token) {
        // Logged-in user: use server API
        await apiClearCart(token);
      } else {
        // Guest user: clear localStorage
        localStorage.removeItem(GUEST_CART_KEY);
      }
      setCart({ items: [], subtotal: 0, total_items: 0 });
    } catch (error) {
      if(process.env.NODE_ENV==='development') if(process.env.NODE_ENV!=="production") console.error('Failed to clear cart:', error);
    }
  };

  const value = {
    cart,
    loading,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    refreshCart: token ? fetchCart : loadGuestCart
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
